import { ValidateUrl, capitalizeFirstLetter } from '@cdab/utils'
import { TdsButton, TdsModal, TdsTextField } from '@scania/tegel-react'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EditorLinkModalProps } from './editor-link-modal.types'

export const EditorLinkModal = React.forwardRef<
  HTMLTdsModalElement,
  EditorLinkModalProps
>(({ text, onClose, onSubmit }, ref) => {
  const { t } = useTranslation(['common', 'errors'])
  const [textState, setTextState] = useState(text)
  const [urlState, setUrlState] = useState('')
  const [textErrorState, setTextErrorState] = useState(true)
  const [urlErrorState, setUrlErrorState] = useState(true)

  const minCharsText = 1

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      const isTextValid = textState.length > minCharsText
      const isUrlValid = ValidateUrl(urlState)

      setTextErrorState(isTextValid)
      setUrlErrorState(isUrlValid)

      if (isTextValid && isUrlValid) {
        onSubmit(textState, urlState)
        onClose()
      }
    },
    [onSubmit, textState, urlState, onClose]
  )

  const handleClose = () => {
    onClose()
  }

  return (
    <TdsModal ref={ref} size='md' actionsPosition='static' header='Add link'>
      <div slot='body' className='tds-u-mt3'>
        <div className='tds-u-mt3'>
          <TdsTextField
            placeholder='Text'
            label='Text'
            labelPosition='outside'
            value={textState}
            onTdsInput={e => setTextState(e.target.value)}
            onTdsBlur={e =>
              setTextErrorState(e.target.value.length > minCharsText)
            }
            state={!textErrorState ? 'error' : undefined}
            helper={
              !textErrorState
                ? t('message.validation-error', {
                    chars: minCharsText.toString(),
                    ns: 'errors'
                  })
                : undefined
            }
          />
        </div>

        <div className='tds-u-mt3'>
          <TdsTextField
            placeholder='URL'
            label='URL'
            labelPosition='outside'
            value={urlState}
            onTdsInput={e => setUrlState(e.target.value)}
            onTdsBlur={e => setUrlErrorState(ValidateUrl(e.target.value))}
            state={!urlErrorState ? 'error' : undefined}
            helper={
              !urlErrorState
                ? t('message.url-not-valid', {
                    ns: 'errors'
                  })
                : undefined
            }
          />
        </div>
      </div>

      <span slot='actions' className='tds-u-flex tds-u-gap2'>
        <TdsButton
          disabled={!textErrorState || !urlErrorState}
          text={capitalizeFirstLetter(t('save'))}
          onClick={handleFormSubmit}
        />
        <TdsButton
          variant='secondary'
          onClick={handleClose}
          text={capitalizeFirstLetter(t('close'))}
        />
      </span>
    </TdsModal>
  )
})
